import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSession } from '@clerk/clerk-react';
import { Card, CardContent } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Spinner } from 'components/ui/spinner';
import { toast } from 'react-toastify';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { useUsers } from 'shared/contexts/users.context';
import { API_URL } from 'constants/index';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  ArrowLeft,
  CalendarBlank,
  CheckCircle,
  UserCircle,
  FileText,
  Trash,
  UploadSimple,
  SpinnerGap,
  Buildings,
  MapPin,
  Wrench,
  PencilSimple,
  FloppyDisk,
} from '@phosphor-icons/react';
import {
  MaintenanceEventStatus,
  MaintenanceEventStatusLabels,
} from 'shared/types/asset';
import * as Sentry from '@sentry/react';

interface MaintenanceEventDetail {
  id: number;
  scheduleId: number;
  assetId: string;
  dueDate: string;
  completedDate?: string;
  status: MaintenanceEventStatus;
  summary?: string;
  supplierReport?: string;
  reporterName?: string;
  reporterEmail?: string;
  reporterPhone?: string;
  validatedBy?: string;
  sharePointFileUrl?: string;
  reportFilePath?: string;
  schedule?: {
    id: number;
    name: string;
    supplier?: { name: string; id: string };
  };
  asset?: {
    id: string;
    name: string;
    building?: { id: string };
    unit?: { id: string };
    room?: { id: string; name?: string };
  };
}

interface EventFile {
  id: number;
  fileLogId: number;
  description?: string;
  fileLog?: {
    id: number;
    sharePointFileUrl?: string;
    summary?: string;
  };
}

export function MaintenanceEventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { session } = useSession();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { users } = useUsers();
  const [event, setEvent] = useState<MaintenanceEventDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<EventFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState<number | null>(null);
  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState('');
  const [savingSummary, setSavingSummary] = useState(false);

  const fetchEvent = useCallback(async () => {
    if (!session || !eventId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/maintenance-events/${eventId}`,
        { method: 'GET' },
        session,
      );
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message || `Event niet gevonden (${res.status})`);
      }
      setEvent(await res.json());
    } catch (err: any) {
      Sentry.captureException(err, { extra: { context: 'fetchMaintenanceEventDetail', eventId } });
      setError(err.message || 'Kon onderhoudsgebeurtenis niet laden');
    } finally {
      setLoading(false);
    }
  }, [session, eventId]);

  const fetchFiles = useCallback(async () => {
    if (!session || !eventId) return;
    setLoadingFiles(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/maintenance-events/${eventId}/files`,
        { method: 'GET' },
        session,
      );
      if (res.ok) {
        setFiles(await res.json());
      }
    } catch (err: any) {
      Sentry.captureException(err, { extra: { context: 'fetchMaintenanceEventFiles', eventId } });
    } finally {
      setLoadingFiles(false);
    }
  }, [session, eventId]);

  useEffect(() => {
    fetchEvent();
    fetchFiles();
  }, [fetchEvent, fetchFiles]);

  const handleUploadFile = useCallback(async (file: File) => {
    if (!session || !eventId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('reportFile', file);
      const res = await fetchWithAuth(
        `${API_URL}/maintenance-events/${eventId}/files`,
        { method: 'POST', body: formData },
        session,
      );
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message || 'Upload mislukt');
      }
      toast.success('Bestand succesvol geüpload');
      await fetchFiles();
    } catch (err: any) {
      Sentry.captureException(err, { extra: { context: 'uploadMaintenanceEventFile', eventId } });
      toast.error(err.message || 'Er is een fout opgetreden bij het uploaden');
    } finally {
      setUploading(false);
    }
  }, [session, eventId, fetchFiles]);

  const handleDeleteFile = useCallback(async (fileLogId: number) => {
    if (!session || !eventId) return;
    setDeletingFileId(fileLogId);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/maintenance-events/${eventId}/files/${fileLogId}`,
        { method: 'DELETE' },
        session,
      );
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message || 'Verwijderen mislukt');
      }
      toast.success('Bestand verwijderd');
      await fetchFiles();
    } catch (err: any) {
      Sentry.captureException(err, { extra: { context: 'deleteMaintenanceEventFile', eventId, fileLogId } });
      toast.error(err.message || 'Er is een fout opgetreden bij het verwijderen');
    } finally {
      setDeletingFileId(null);
    }
  }, [session, eventId, fetchFiles]);

  const handleSaveSummary = useCallback(async () => {
    if (!session || !eventId) return;
    setSavingSummary(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/maintenance-events/${eventId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ summary: summaryDraft }),
        },
        session,
      );
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message || 'Opslaan mislukt');
      }
      const updated = await res.json();
      setEvent(updated);
      setEditingSummary(false);
      toast.success('Samenvatting opgeslagen');
    } catch (err: any) {
      Sentry.captureException(err, { extra: { context: 'updateMaintenanceEventSummary', eventId } });
      toast.error(err.message || 'Er is een fout opgetreden bij het opslaan');
    } finally {
      setSavingSummary(false);
    }
  }, [session, eventId, summaryDraft]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner className="h-6 w-6" />
        <span className="ml-2 text-muted-foreground">Laden...</span>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft size={16} className="mr-1" />
          Terug
        </Button>
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">{error || 'Gebeurtenis niet gevonden'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusVariant = event.status === MaintenanceEventStatus.VALIDATED ? 'default' :
    event.status === MaintenanceEventStatus.REPORTED ? 'secondary' : 'outline';
  const StatusIcon = event.status === MaintenanceEventStatus.VALIDATED ? CheckCircle : UserCircle;
  const validatedByUser = event.validatedBy ? users.find((u: any) => u.id === event.validatedBy) : null;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-3">
          <ArrowLeft size={16} className="mr-1" />
          Terug
        </Button>
        <div className="flex items-center gap-3">
          <Wrench size={24} className="text-muted-foreground" />
          <div className="flex-1">
            <h1 className="text-xl font-bold">
              {event.schedule?.name || `Gebeurtenis #${event.id}`}
            </h1>
            <p className="text-sm text-muted-foreground">
              Onderhoudsgebeurtenis #{event.id}
            </p>
          </div>
          <Badge variant={statusVariant} className="flex items-center gap-1">
            <StatusIcon size={14} />
            {MaintenanceEventStatusLabels[event.status]}
          </Badge>
        </div>
      </div>

      {/* Info grid */}
      <Card>
        <CardContent className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {event.asset && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Toestel</p>
                <Link
                  to={`/asset/${event.asset.id}`}
                  className="text-sm font-medium text-foreground hover:text-primary hover:underline"
                >
                  {event.asset.name || event.asset.id}
                </Link>
              </div>
            )}
            {event.asset?.building && (
              <div className="flex items-start gap-1.5">
                <Buildings size={14} className="text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Gebouw</p>
                  <p className="text-sm font-medium">{event.asset.building.id}</p>
                </div>
              </div>
            )}
            {(event.asset?.unit || event.asset?.room) && (
              <div className="flex items-start gap-1.5">
                <MapPin size={14} className="text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Locatie</p>
                  <p className="text-sm font-medium">
                    {[event.asset.unit?.id, event.asset.room?.name || event.asset.room?.id]
                      .filter(Boolean)
                      .join(' – ')}
                  </p>
                </div>
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground mb-1">Vervaldatum</p>
              <div className="flex items-center gap-1.5">
                <CalendarBlank size={14} className="text-muted-foreground" />
                <p className="text-sm font-medium">
                  {format(new Date(event.dueDate), 'd MMMM yyyy', { locale: nl })}
                </p>
              </div>
            </div>
            {event.completedDate && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Gevalideerd op</p>
                <p className="text-sm font-medium">
                  {format(new Date(event.completedDate), 'd MMMM yyyy', { locale: nl })}
                </p>
              </div>
            )}
            {event.validatedBy && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Gevalideerd door</p>
                <div className="flex items-center gap-2">
                  {validatedByUser?.image_url ? (
                    <img
                      src={validatedByUser.image_url}
                      alt={`${validatedByUser.first_name || ''} ${validatedByUser.last_name || ''}`}
                      className="w-6 h-6 rounded-full object-cover"
                    />
                  ) : (
                    <UserCircle size={20} className="text-muted-foreground" />
                  )}
                  <p className="text-sm font-medium">
                    {validatedByUser
                      ? `${validatedByUser.first_name || ''} ${validatedByUser.last_name || ''}`.trim()
                      : 'Onbekende gebruiker'}
                  </p>
                </div>
              </div>
            )}
            {event.schedule?.supplier && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Leverancier</p>
                <p className="text-sm font-medium">{event.schedule.supplier.name}</p>
              </div>
            )}
            {event.reporterName && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Rapporteur</p>
                <p className="text-sm font-medium">{event.reporterName}</p>
                {event.reporterEmail && (
                  <p className="text-xs text-muted-foreground">{event.reporterEmail}</p>
                )}
                {event.reporterPhone && (
                  <p className="text-xs text-muted-foreground">{event.reporterPhone}</p>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary & Supplier Report */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium text-muted-foreground">Samenvatting</p>
              {!editingSummary ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setSummaryDraft(event.summary || '');
                    setEditingSummary(true);
                  }}
                >
                  <PencilSimple size={12} className="mr-1" />
                  Bewerken
                </Button>
              ) : (
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => setEditingSummary(false)}
                    disabled={savingSummary}
                  >
                    Annuleren
                  </Button>
                  <Button
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={handleSaveSummary}
                    disabled={savingSummary}
                  >
                    <FloppyDisk size={12} className="mr-1" />
                    {savingSummary ? 'Opslaan...' : 'Opslaan'}
                  </Button>
                </div>
              )}
            </div>
            {editingSummary ? (
              <textarea
                className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={summaryDraft}
                onChange={(e) => setSummaryDraft(e.target.value)}
                placeholder="Voeg een samenvatting toe..."
              />
            ) : (
              <p className="text-sm whitespace-pre-wrap">
                {event.summary || <span className="text-muted-foreground italic">Geen samenvatting</span>}
              </p>
            )}
          </div>
          {event.supplierReport && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Leveranciersrapport</p>
              <p className="text-sm whitespace-pre-wrap text-blue-700">{event.supplierReport}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Files section */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FileText size={18} className="text-muted-foreground" />
              <h3 className="text-sm font-semibold">Bestanden</h3>
            </div>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(ev) => {
                  const file = ev.target.files?.[0];
                  if (file) handleUploadFile(file);
                  ev.target.value = '';
                }}
              />
              <Button
                variant="outline"
                size="sm"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
              >
                <UploadSimple size={14} className="mr-1" />
                {uploading ? 'Uploaden...' : 'Bestand uploaden'}
              </Button>
            </div>
          </div>

          {/* Legacy single file */}
          {event.sharePointFileUrl && files.length === 0 && (
            <a
              href={event.sharePointFileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-sky-600 hover:text-sky-800 hover:underline mb-3"
            >
              <FileText size={14} />
              Rapport bekijken (SharePoint)
            </a>
          )}

          {loadingFiles && files.length === 0 && (
            <div className="flex items-center gap-2 py-4 justify-center text-muted-foreground">
              <Spinner className="h-4 w-4" />
              <span className="text-sm">Bestanden laden...</span>
            </div>
          )}

          {!loadingFiles && files.length === 0 && !event.sharePointFileUrl && (
            <div className="text-center py-6 border-2 border-dashed rounded-lg">
              <FileText size={32} className="mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Nog geen bestanden toegevoegd</p>
            </div>
          )}

          {files.length > 0 && (
            <div className="space-y-2">
              {files.map((f) => {
                const fileUrl = f.fileLog?.sharePointFileUrl;
                const fileName = f.description || f.fileLog?.summary || `Bestand #${f.fileLogId}`;
                return (
                  <div
                    key={f.id}
                    className="flex items-center justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <FileText size={16} className="text-sky-600 shrink-0" />
                      {fileUrl ? (
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-sky-600 hover:text-sky-800 hover:underline truncate"
                          title={fileName}
                        >
                          {fileName}
                        </a>
                      ) : (
                        <span className="text-sm text-muted-foreground truncate" title={fileName}>
                          {fileName}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-red-600 transition-colors shrink-0 ml-2"
                      disabled={deletingFileId === f.fileLogId}
                      onClick={() => handleDeleteFile(f.fileLogId)}
                      title="Verwijder bestand"
                    >
                      {deletingFileId === f.fileLogId ? (
                        <SpinnerGap size={14} className="animate-spin" />
                      ) : (
                        <Trash size={14} />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
